import { useId, useState } from 'react';
import { Megaphone, Send, Trash2, X } from 'lucide-react';
import { useAuthStore } from '../../../store/auth.js';
import { Button } from '../../../shell/Button.js';
import { Input, Textarea } from '../../../shell/Field.js';
import { toast } from '../../../shell/Toast.js';
import { formatDateTime } from '../../../lib/format.js';
import { ApiRequestError } from '../../../lib/api.js';
import {
  useAnnouncements,
  useCreateAnnouncement,
  useDeleteAnnouncement,
} from '../announcements.js';

export function AnnouncementStrip() {
  const { user } = useAuthStore();
  const isAdmin = user?.role === 'Admin';
  const { data, isLoading } = useAnnouncements();
  const createAnnouncement = useCreateAnnouncement();
  const deleteAnnouncement = useDeleteAnnouncement();
  const [composing, setComposing] = useState(false);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const formId = useId();

  const latest = data?.items[0];

  if (!isAdmin && !isLoading && !latest) {
    return null;
  }

  const closeComposer = () => {
    setComposing(false);
    setTitle('');
    setBody('');
  };

  const handlePublish = async (event: { preventDefault(): void }) => {
    event.preventDefault();
    const nextTitle = title.trim();
    const nextBody = body.trim();
    if (!nextTitle || !nextBody) {
      toast('error', 'Add a title and a message before sending.');
      return;
    }
    try {
      await createAnnouncement.mutateAsync({ title: nextTitle, body: nextBody });
      closeComposer();
      toast('success', 'Announcement sent company-wide.');
    } catch (err) {
      const msg = err instanceof ApiRequestError ? err.body.message : 'Failed to send announcement.';
      toast('error', msg);
    }
  };

  const handleDelete = async () => {
    if (!latest) return;
    if (!window.confirm(`Remove “${latest.title}”? It will disappear from every dashboard.`)) {
      return;
    }
    try {
      await deleteAnnouncement.mutateAsync(latest.id);
      toast('success', 'Announcement removed.');
    } catch (err) {
      const msg = err instanceof ApiRequestError ? err.body.message : 'Failed to remove announcement.';
      toast('error', msg);
    }
  };

  return (
    <section
      className="dashboard-announce"
      data-open={composing ? '1' : '0'}
      aria-labelledby="company-announce-title"
    >
      <div className="dashboard-announce-bar">
        <Megaphone className="dashboard-announce-icon" aria-hidden="true" />
        {isLoading ? (
          <p id="company-announce-title" className="dashboard-announce-idle">
            Loading announcement…
          </p>
        ) : latest ? (
          <div className="dashboard-announce-copy-wrap">
            <p id="company-announce-title" className="dashboard-announce-heading">
              {latest.title}
            </p>
            <p className="dashboard-announce-copy">{latest.body}</p>
            <p className="dashboard-announce-meta">
              {latest.createdByName} · {formatDateTime(latest.createdAt)}
            </p>
          </div>
        ) : (
          <p id="company-announce-title" className="dashboard-announce-idle">
            Company announcement
          </p>
        )}

        {isAdmin && (
          <div className="dashboard-announce-tools">
            {latest && !composing && (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                aria-label={`Remove ${latest.title}`}
                loading={deleteAnnouncement.isPending}
                onClick={handleDelete}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
            <Button
              type="button"
              size="sm"
              variant={composing ? 'secondary' : 'primary'}
              aria-expanded={composing}
              aria-controls={formId}
              onClick={() => (composing ? closeComposer() : setComposing(true))}
            >
              {composing ? <X className="h-4 w-4" /> : <Megaphone className="h-4 w-4" />}
              {composing ? 'Cancel' : latest ? 'Update' : 'Post'}
            </Button>
          </div>
        )}
      </div>

      {isAdmin && composing && (
        <form id={formId} className="dashboard-announce-form" onSubmit={handlePublish}>
          <Input
            label="Headline"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            maxLength={160}
            placeholder="Office closed Friday"
            autoFocus
            required
          />
          <Textarea
            label="Message"
            value={body}
            onChange={(event) => setBody(event.target.value)}
            maxLength={2000}
            rows={3}
            placeholder="What should everyone know?"
            required
          />
          <div className="dashboard-announce-actions">
            <Button type="submit" loading={createAnnouncement.isPending}>
              <Send className="h-4 w-4" />
              Send to company
            </Button>
          </div>
        </form>
      )}
    </section>
  );
}
